import {
    useState, useRef, useEffect,
} from 'react';
import Box from '@mui/material/Box';
import Backdrop from '@mui/material/Backdrop';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import ClearIcon from '@mui/icons-material/Clear';
import WarningIcon from '@mui/icons-material/Warning';
import { App } from 'BMapsSrc/BMapsApp';
import { loadFiles } from 'BMapsSrc/paste_drag';
import {
    isSmilesLine, isAminoAcidSequence, isDnaSequence, isRnaSequence, isCcdCode,
} from 'BMapsUtil/mol_format_utils';
import { DialogCancelButton, DialogSubmitButton } from 'BMapsUI/common/DialogActionButtons';
import { withTooltip, wrapForDisabledTooltip } from 'BMapsUI/common/helpers';
import BMapsTooltip from 'BMapsUI/BMapsTooltip';
import { TextArea } from 'BMapsUI/common/TextFields';
import Select from 'BMapsUI/common/Select';
import { InputFile, PlaceHolder, UploadButton } from 'BMapsUI/dialog/ImportPane/import_components';
import FormControlContainer from 'BMapsUI/dialog/ImportPane/base/FormControlContainer';
import { EditInPlaceInput, TextOrTooltip } from 'BMapsUI/tables/TableCells';

// Per-type-id format check — a light, best-effort heuristic (character-set
// matching, not a real molecular/structural parse), the same kind of check
// protein folding's own `checkSequenceType` already does. Kept here, not on
// a job's own `types` metadata, so a job still only ever names a type by id
// (no functions) — this widget owns what "looks like smiles"/"looks like a
// protein sequence" means for a given type id, ready for the type ids
// folding's own sequence input already uses if this widget replaces it.
const TYPE_FORMAT_CHECKS = {
    ligand_smiles: isSmilesLine,
    protein: isAminoAcidSequence,
    dna: isDnaSequence,
    rna: isRnaSequence,
    ccd: isCcdCode,
};

function matchesFormat(typeId, data) {
    const check = TYPE_FORMAT_CHECKS[typeId];
    return !check || check(data);
}

// With more than one type, a mismatch is described against "the selected
// type" — matching protein folding's own wording — since the user actually
// picked it from a dropdown. With only one type, there's nothing to have
// selected, so the message names the expected format directly instead.
function mismatchDescription(types, type) {
    return types.length > 1
        ? `the selected type (${type.label || type.placeholder})`
        : `the expected format for ${type.label || type.placeholder}`;
}

// Per-type-id workspace lookup — how "Add Component from Workspace" finds
// candidates for a given supported type. Kept here, not on the job's own
// `types` metadata, so a job definition never has to import `App`/reach
// into the workspace itself — it just names which types it supports; this
// widget owns fetching each one's candidates, the same way `pocket`
// already self-fetches rather than being handed data.
const WORKSPACE_OPTIONS_BY_TYPE = {
    ligand_smiles: () => App.Workspace.getLoadedCompounds().map((compound) => ({
        key: compound.resSpec,
        label: compound.displayName,
        data: compound.getUnnamedSmiles(),
    })),
};

function typeFor(types, typeId) {
    return types.find((t) => t.id === typeId) || types[0];
}

// Joins each supported type's display name into one natural-language
// phrase — "ligand smiles" for one type, "protein, DNA, RNA, or ligand
// smiles" for several — so the info tooltip below stays accurate to
// whichever types a given job actually declares, rather than a fixed list.
function describeTypes(types) {
    const names = types.map((t) => t.label || t.placeholder);
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} or ${names[1]}`;
    return `${names.slice(0, -1).join(', ')}, or ${names[names.length - 1]}`;
}

// Matches protein folding's own "Add Sequence / Ligand" info tooltip —
// same `withTooltip` HOC, but with the icon cell's hardcoded 15px top
// padding zeroed out (tuned for label+input stacks, not a plain bold
// label), which is what made folding's version sit slightly misaligned.
const LabelWithInfoTooltip = withTooltip('strong');

function addAreaTooltip(types) {
    const typesText = describeTypes(types);
    return (
        <>
            Enter
            {' '}
            {typesText}
            , one per line.
            <br />
            Or drag &amp; drop or upload a text file with
            {' '}
            {typesText}
            .
            <br />
            Then click &quot;Include&quot; to add them to the list below.
        </>
    );
}

// The expanded "add" overlay — a backdrop (disabling the rest of the form,
// same as protein folding's own add-sequence flow) plus a paper holding a
// type-aware textarea (paste, drag-and-drop, or upload a text file), an
// "Add Component from Workspace" picker (only for types with a registered
// workspace lookup), a type selector (only when more than one type is
// supported), and Include/Cancel actions.
function AddEntryArea({
    types, addLabel, onInclude, onClose,
}) {
    const [draft, setDraft] = useState('');
    const [draftType, setDraftType] = useState(types[0].id);
    const [loading, setLoading] = useState(false);
    const uploadInputRef = useRef(null);
    const paperRef = useRef(null);
    const currentType = typeFor(types, draftType);

    // Mounts only once the user clicks "Add", so a mount-time scroll is
    // enough to bring it into view — matches protein folding's own
    // add-sequence area.
    useEffect(() => { paperRef.current?.scrollIntoView(); }, []);

    const workspaceOptions = types
        .filter((t) => WORKSPACE_OPTIONS_BY_TYPE[t.id])
        .flatMap((t) => (
            WORKSPACE_OPTIONS_BY_TYPE[t.id]().map((option) => ({ ...option, type: t.id }))
        ));

    const lines = draft.split('\n').map((line) => line.trim()).filter((line) => line !== '');
    const unrecognizedCount = lines.filter((line) => !matchesFormat(draftType, line)).length;
    let includeTooltip = '';
    if (lines.length === 0) {
        includeTooltip = 'Enter at least one entry to include';
    } else if (unrecognizedCount > 0) {
        const linesLabel = unrecognizedCount === 1 ? 'line does' : 'lines do';
        includeTooltip = `Note: ${unrecognizedCount} input ${linesLabel} not match `
            + `${mismatchDescription(types, currentType)}. Please double-check before submission.`;
    }

    async function stageFiles(files) {
        setLoading(true);
        const text = await loadFiles(files);
        setLoading(false);
        setDraft((prev) => (prev ? `${prev}\n${text.join('\n')}` : text.join('\n')));
    }

    // Adds to the draft, same as typing it in — the user still has to hit
    // "Include" to actually commit it, matching every other way of getting
    // text into this box (paste, upload).
    function handleWorkspaceSelect(option) {
        setDraft((prev) => (prev ? `${prev}\n${option.data}` : option.data));
        setDraftType(option.type);
    }

    function handleInclude() {
        if (lines.length === 0) return;
        onInclude(lines.map((data) => ({ type: draftType, data, count: 1 })));
        onClose();
    }

    return (
        <>
            <Backdrop open onClick={onClose} sx={{ zIndex: 2 }} />
            <Paper
                ref={paperRef}
                sx={{
                    padding: '0 1rem 1rem 1rem', position: 'relative', zIndex: 2,
                }}
            >
                <Box sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '1rem',
                }}
                >
                    <LabelWithInfoTooltip
                        tooltip={addAreaTooltip(types)}
                        WrapperProps={{ style: { alignItems: 'center' } }}
                        IconWrapperProps={{
                            style: {
                                width: '3rem', // match default width
                                justifyContent: 'center',
                                paddingTop: 0,
                            },
                        }}
                    >
                        {addLabel}
                    </LabelWithInfoTooltip>
                    <IconButton onClick={onClose}><ClearIcon /></IconButton>
                </Box>
                {workspaceOptions.length > 0 && (
                    <Select
                        label="Add from Workspace"
                        slotProps={{ select: { native: false } }}
                        sx={{ width: 'fit-content', minWidth: '14rem', marginTop: '1rem' }}
                    >
                        {workspaceOptions.map((option) => {
                            const selectOption = () => handleWorkspaceSelect(option);
                            return (
                                <MenuItem key={option.key} onClick={selectOption}>
                                    {option.label}
                                </MenuItem>
                            );
                        })}
                    </Select>
                )}
                <Box sx={{ marginTop: '1rem' }}>
                    <PlaceHolder $show={draft === ''}>
                        {loading ? 'Loading...' : (
                            <>
                                Enter
                                {' '}
                                {currentType.placeholder}
                                , one per line.
                                <br />
                                Or Drag & Drop or
                                {' '}
                                <UploadButton
                                    disableRipple
                                    onClick={() => uploadInputRef.current.click()}
                                >
                                    upload
                                </UploadButton>
                                {' '}
                                {currentType.filetypes.join(', ')}
                                {' '}
                                or other text files with
                                {' '}
                                {currentType.placeholder}
                                .
                            </>
                        )}
                    </PlaceHolder>
                    <TextArea
                        autoFocus
                        multiline
                        minRows={1}
                        value={draft}
                        onChange={(evt) => setDraft(evt.target.value)}
                        onDrop={(evt) => {
                            evt.preventDefault();
                            if (evt.dataTransfer.files.length > 0) {
                                stageFiles(evt.dataTransfer.files);
                            }
                        }}
                        sx={{ '& textarea': { resize: 'vertical', minHeight: '6rem' } }}
                    />
                    <FormControlContainer>
                        <InputFile
                            inputRef={uploadInputRef}
                            type="file"
                            onChange={(evt) => stageFiles(evt.target.files)}
                        />
                    </FormControlContainer>
                </Box>
                {types.length > 1 && (
                    <Select
                        label="Component Type"
                        slotProps={{ select: { native: false } }}
                        value={draftType}
                        onChange={(evt) => setDraftType(evt.target.value)}
                        sx={{ width: '50%', marginTop: '1rem' }}
                    >
                        {types.map((t) => (
                            <MenuItem key={t.id} value={t.id}>{t.label || t.placeholder}</MenuItem>
                        ))}
                    </Select>
                )}
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    gap: '0.5rem',
                    marginTop: '1rem',
                }}
                >
                    <BMapsTooltip title={includeTooltip}>
                        {wrapForDisabledTooltip(
                            <DialogSubmitButton
                                onClick={handleInclude}
                                disabled={lines.length === 0}
                                sx={{ flex: '1 1 auto' }}
                            >
                                Include Component
                            </DialogSubmitButton>,
                            { WrapperProps: { style: { display: 'flex', flex: '1 1 auto' } } }
                        )}
                    </BMapsTooltip>
                    <DialogCancelButton onClick={onClose} sx={{ flex: '1 1 auto' }}>
                        Cancel
                    </DialogCancelButton>
                </Box>
            </Paper>
        </>
    );
}

// How many copies of this entry to send — mirrors protein folding's own
// per-chain count column (1-10), only shown when the job actually wants it
// (`descriptor.withCount`), since not every entry list represents something
// that makes sense to repeat.
const COUNT_OPTIONS = Array.from({ length: 10 }, (_, i) => i + 1);

function EntryRow({
    entry, types, withCount, onUpdate, onRemove,
}) {
    const [editingData, setEditingData] = useState(false);
    const type = typeFor(types, entry.type);
    const formatWarning = !matchesFormat(entry.type, entry.data)
        ? `The provided data doesn't match ${mismatchDescription(types, type)}. `
            + 'Please double-check before submission.'
        : '';
    return (
        <TableRow>
            {types.length > 1 && <TableCell>{type.label || type.placeholder}</TableCell>}
            <TableCell sx={{ wordBreak: 'break-all' }}>
                {!editingData ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BMapsTooltip title="Edit component data">
                            <IconButton size="small" onClick={() => setEditingData(true)}>
                                <Edit fontSize="small" />
                            </IconButton>
                        </BMapsTooltip>
                        <TextOrTooltip text={entry.data} limit={40} />
                    </Box>
                ) : (
                    <EditInPlaceInput
                        Component={TextArea}
                        value={entry.data}
                        setValue={(value) => {
                            onUpdate({ ...entry, data: value });
                            setEditingData(false);
                        }}
                        stopEditing={() => setEditingData(false)}
                    />
                )}
            </TableCell>
            {withCount && (
                <TableCell>
                    <Select
                        size="small"
                        value={entry.count || 1}
                        slotProps={{ select: { native: false } }}
                        onChange={(evt) => onUpdate({ ...entry, count: evt.target.value })}
                    >
                        {COUNT_OPTIONS.map((count) => (
                            <MenuItem key={count} value={count}>{count}</MenuItem>
                        ))}
                    </Select>
                </TableCell>
            )}
            <TableCell align="right">
                {!!formatWarning && (
                    <BMapsTooltip title={formatWarning}>
                        <IconButton size="small" sx={{ color: 'red' }}>
                            <WarningIcon fontSize="small" />
                        </IconButton>
                    </BMapsTooltip>
                )}
                <BMapsTooltip title="Remove component from input">
                    <IconButton size="small" onClick={onRemove}>
                        <ClearIcon fontSize="small" />
                    </IconButton>
                </BMapsTooltip>
            </TableCell>
        </TableRow>
    );
}

function EntryTable({
    entries, types, withCount, onUpdate, onRemove, onClearAll,
}) {
    return (
        <TableContainer sx={{ maxHeight: '20rem', marginBottom: '0.5rem' }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        {types.length > 1 && <TableCell>Type</TableCell>}
                        <TableCell>Data</TableCell>
                        {withCount && <TableCell>Count</TableCell>}
                        <TableCell align="right">
                            <BMapsTooltip title="Remove everything">
                                <IconButton size="small" onClick={onClearAll}>
                                    <Delete fontSize="small" />
                                </IconButton>
                            </BMapsTooltip>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {entries.map((entry, i) => (
                        <EntryRow
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${i}-${entry.type}-${entry.data}`}
                            entry={entry}
                            types={types}
                            withCount={withCount}
                            onUpdate={(next) => onUpdate(i, next)}
                            onRemove={() => onRemove(i)}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

/**
 * A list of typed entries (ligand SMILES, protein sequences, ...) built up
 * through an "Add" flow modeled on protein folding's own sequence input:
 * collapsed to a button by default; clicking it opens a backdrop-blocked
 * overlay with a type-aware textarea (paste, drag-and-drop, or upload a
 * text file), an "Add Component from Workspace" picker (only for types
 * with a registered workspace lookup), a type selector (only when more
 * than one type is supported — with one, there's nothing to pick between,
 * so it's omitted, and the textarea's own description only ever mentions
 * that one type, never formats it doesn't accept), and an Include/Cancel
 * pair. Included entries land in an editable, removable table — the type
 * column is likewise omitted when there's only one possible type. Every
 * entry also carries a `count` (default 1); the table's Count column only
 * renders when the job opts in (`descriptor.withCount`), since not every
 * entry list represents something worth repeating.
 * `formState[key]` is always an array of `{ type, data, count }`.
 *
 * A job declares which types it supports as pure metadata (`types:
 * [{ id, label?, unit, unitPlural, placeholder, filetypes }]`) — no
 * functions, no `App` import — this widget owns turning a type id into
 * workspace candidates (`WORKSPACE_OPTIONS_BY_TYPE`) and a format check
 * (`TYPE_FORMAT_CHECKS`), the same way `pocket`/other widgets self-fetch
 * rather than being handed data. `placeholder` doubles as the type's
 * user-facing display name (Type column, type selector, format-mismatch
 * messages) whenever `label` isn't given — `id` is never shown to the
 * user, only used for lookups and React keys; `label` exists only as an
 * override for a type whose `placeholder` is too long for a table
 * column/dropdown, not something every type needs to declare. The format
 * check is the same kind of light, best-effort heuristic protein folding's
 * own `checkSequenceType` uses (character-set matching, not a real
 * molecular parse) — a mismatch warns (in the add flow's Include tooltip,
 * and per-row via a warning icon once included) but never blocks; a type
 * with no registered check is treated as always matching.
 */
export default function TypedEntryList({ descriptor, formState, setFormState }) {
    const {
        key, addLabel, types, withCount,
    } = descriptor;
    const entries = formState[key] || [];
    const [adding, setAdding] = useState(false);

    function addEntries(newEntries) {
        setFormState({ [key]: [...entries, ...newEntries] });
    }
    function updateEntry(index, next) {
        setFormState({ [key]: entries.map((entry, i) => (i === index ? next : entry)) });
    }
    function removeEntry(index) {
        setFormState({ [key]: entries.filter((_, i) => i !== index) });
    }

    return (
        <Box sx={{ marginTop: '1rem' }}>
            {entries.length > 0 && (
                <EntryTable
                    entries={entries}
                    types={types}
                    withCount={withCount}
                    onUpdate={updateEntry}
                    onRemove={removeEntry}
                    onClearAll={() => setFormState({ [key]: [] })}
                />
            )}
            {adding ? (
                <AddEntryArea
                    types={types}
                    addLabel={addLabel}
                    onInclude={addEntries}
                    onClose={() => setAdding(false)}
                />
            ) : (
                <DialogCancelButton sx={{ width: '100%' }} onClick={() => setAdding(true)}>
                    <Add />
                    {' '}
                    {addLabel}
                </DialogCancelButton>
            )}
        </Box>
    );
}
